import React, { useEffect, useState, useRef } from 'react';

const CrowdCalendar = () => {
  // Load CSS file for enhanced widget
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/crowd-calendar-widget.css';
    link.id = 'crowd-calendar-css';
    document.head.appendChild(link);
    
    return () => {
      // Cleanup CSS when component unmounts
      const existingLink = document.getElementById('crowd-calendar-css');
      if (existingLink) {
        existingLink.remove();
      }
    };
  }, []);

  const containerRef = useRef(null);
  const widgetRef = useRef(null);

  useEffect(() => {
    // Load the enhanced crowd calendar widget script if not already loaded
    const loadWidget = async () => {
      // Check if the widget class is already available
      if (window.EnhancedCrowdCalendarWidget) {
        console.log('Widget class already exists, initializing...');
        initializeWidget();
        return;
      }

      // Check if script is already loading/loaded
      const existingScript = document.querySelector('script[src="/crowd-calendar-widget-enhanced.js"]');
      if (existingScript) {
        console.log('Script already exists, waiting for load...');
        if (existingScript.complete || existingScript.readyState === 'complete') {
          initializeWidget();
        } else {
          existingScript.onload = () => initializeWidget();
        }
        return;
      }

      // Load the widget script
      console.log('Loading crowd calendar widget script...');
      const script = document.createElement('script');
      script.src = '/crowd-calendar-widget-updated.js';
      script.id = 'crowd-calendar-script';
      script.onload = () => {
        console.log('Updated crowd calendar widget script loaded');
        checkAndInitialize();
      };
      script.onerror = (error) => {
        console.error('=== Script Load Error ===');
        console.error('Failed to load crowd calendar widget:', error);
        console.error('Script src:', script.src);
        console.error('Script readyState:', script.readyState);
        if (containerRef.current) {
          containerRef.current.innerHTML = `
            <div style="text-align: center; padding: 40px; color: #666;">
              <h3>Unable to load crowd calendar</h3>
              <p>Script loading failed. Please refresh the page to try again.</p>
              <p style="font-size: 12px; color: #999;">Error: ${error.message || 'Script load error'}</p>
            </div>
          `;
        }
      };
      console.log('Appending script to head...');
      document.head.appendChild(script);
      console.log('Script element created and appended');
    };

    const checkAndInitialize = () => {
      console.log('=== Checking Updated Widget Availability ===');
      console.log('Container ref current:', !!containerRef.current);
      console.log('Container ID found:', !!document.getElementById('crowd-calendar-widget'));
      console.log('UpdatedCrowdCalendarWidget available:', !!window.UpdatedCrowdCalendarWidget);
      
      if (containerRef.current && window.UpdatedCrowdCalendarWidget) {
        console.log('=== Initializing Updated Widget ===');
        try {
          widgetRef.current = new window.UpdatedCrowdCalendarWidget('crowd-calendar-widget');
          console.log('Updated widget initialized successfully:', !!widgetRef.current);
        } catch (error) {
          console.error('Error initializing updated widget:', error);
          setError('Failed to initialize updated crowd calendar widget');
        }
      } else {
        console.log('=== Updated Widget Not Ready ===');
        console.log('Missing container:', !containerRef.current);
        console.log('Missing updated widget class:', !window.UpdatedCrowdCalendarWidget);
        
        // Retry after a short delay
        setTimeout(checkAndInitialize, 500);
      }
    };

    const initializeWidget = async () => {
      console.log('=== Widget Initialization Debug ===');
      console.log('Container ref:', containerRef.current);
      console.log('Container exists:', !!containerRef.current);
      console.log('Container ID:', containerRef.current?.id);
      console.log('Widget class exists:', !!window.EnhancedCrowdCalendarWidget);
      console.log('Window object keys containing "Enhanced":', Object.keys(window).filter(key => key.includes('Enhanced')));
      console.log('All window keys containing "Crowd":', Object.keys(window).filter(key => key.toLowerCase().includes('crowd')));
      
      if (containerRef.current && window.EnhancedCrowdCalendarWidget) {
        try {
          const containerId = 'crowd-calendar-agent-' + Date.now();
          containerRef.current.id = containerId;
          console.log('Container ID set:', containerId);

          // Initialize the enhanced crowd calendar widget
          console.log('Creating widget instance...');
          widgetRef.current = new window.EnhancedCrowdCalendarWidget(containerId, {
            apiBase: '/api/public/theme-parks' // Use public API endpoint for widget compatibility
          });
          
          // Call the init method to actually start the widget
          console.log('Calling widget init method...');
          await widgetRef.current.init();
          console.log('Widget initialized successfully');
        } catch (error) {
          console.error('Error initializing widget:', error);
          if (containerRef.current) {
            containerRef.current.innerHTML = `
              <div style="text-align: center; padding: 40px; color: #666;">
                <h3>Widget Initialization Error</h3>
                <p>${error.message}</p>
                <button onclick="window.location.reload()" style="margin-top: 10px; padding: 8px 16px; background: #3b82f6; color: white; border: none; border-radius: 4px; cursor: pointer;">
                  Refresh Page
                </button>
              </div>
            `;
          }
        }
      } else {
        console.error('Cannot initialize widget - missing container or widget class');
      }
    };

    loadWidget();

    // Cleanup function
    return () => {
      if (widgetRef.current && widgetRef.current.destroy) {
        widgetRef.current.destroy();
      }
    };
  }, []);

  return (
    <div className="agent-crowd-calendar-wrapper">
      <div className="agent-page-header">
        <h1>🎢 Theme Park Crowd Calendar</h1>
        <p className="agent-page-subtitle">
          Professional crowd intelligence powered by 5.5+ years of historical data
        </p>
      </div>
      
      <div 
        ref={containerRef}
        id="crowd-calendar-widget"
        className="crowd-calendar-container"
        style={{
          minHeight: '600px',
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Loading state while widget initializes */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          flexDirection: 'column',
          color: '#6b7280'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p>Loading crowd calendar...</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .agent-crowd-calendar-wrapper {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }
        
        .agent-page-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .agent-page-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 8px;
        }
        
        .agent-page-subtitle {
          font-size: 1.1rem;
          color: #6b7280;
          margin: 0;
        }
        
        /* Override widget styles for agent interface */
        .crowd-calendar-container .crowd-calendar-enhanced {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }
        
        .crowd-calendar-container .calendar-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          border-radius: 12px !important;
          padding: 20px !important;
          margin-bottom: 20px !important;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .agent-crowd-calendar-wrapper {
            padding: 10px;
          }
          
          .agent-page-header h1 {
            font-size: 2rem;
          }
          
          .crowd-calendar-container {
            padding: 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default CrowdCalendar;
